import {
  AdmonitionDirectiveDescriptor,
  BoldItalicUnderlineToggles,
  CreateLink,
  InsertAdmonition,
  InsertThematicBreak,
  ListsToggle,
  MDXEditor,
  MDXEditorMethods,
  UndoRedo,
  directivesPlugin,
  headingsPlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  markdownShortcutPlugin,
  maxLengthPlugin,
  quotePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
} from '@mdxeditor/editor'
import CheckIcon from '@mui/icons-material/Check'
import { Alert, AlertTitle, Box, Paper, Typography } from '@mui/material'
import Debug from 'debug'
import '@mdxeditor/editor/style.css'
import { useRef, useState } from 'react'
import { useMutation } from 'urql'
import { useDebounceCallback } from 'usehooks-ts'
import { useOrderContext } from '../contexts/OrderContextProvider'
import { graphql } from '../gql'
import DateDisplay from './DateDisplay'

const log = Debug('ela:OrderNotes')
log.log = console.info.bind(console)

const updateNoteMutation = graphql(`
  mutation OrderNotesUpdate($id: Int!, $input: UpdateRfqInput!) {
    UpdateRfq(id: $id, input: $input) {
      id
    }
  }
`)

export default function OrderNotes() {
  const { rfq, fetching, error } = useOrderContext()
  const ref = useRef<MDXEditorMethods>(null)
  const [updateNoteResult, updateNote] = useMutation(updateNoteMutation)
  const [lastSavedDate, setLastSavedDate] = useState<Date | null>(null)

  const handleNoteChange = (value: string) => {
    debouncedCommitNote(value)
  }

  const commitNote = (value: string) => {
    if (!rfq) return
    const val = ref.current?.getMarkdown?.() || ''
    log('Saving note')
    updateNote({
      id: rfq.id,
      input: {
        note: val,
      },
    }).then((result) => {
      if (!result.error) {
        setLastSavedDate(new Date())
      }
    })
  }
  const debouncedCommitNote = useDebounceCallback(commitNote, 500)

  return (
    <Box
      sx={{
        '& .order-notes-content-editable': { minHeight: '20vh' },
        '& .mdxeditor-toolbar': { borderRadius: 0 },
      }}
    >
      <Paper variant="outlined" sx={{ backgroundColor: '#fffef2' }}>
        {updateNoteResult.error && (
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            {updateNoteResult.error.message}
          </Alert>
        )}
        <MDXEditor
          markdown={rfq?.note || ''}
          ref={ref}
          readOnly={Boolean(fetching || error)}
          contentEditableClassName="order-notes-content-editable"
          plugins={[
            directivesPlugin({ directiveDescriptors: [AdmonitionDirectiveDescriptor] }),
            headingsPlugin(),
            quotePlugin(),
            listsPlugin(),
            linkPlugin(),
            linkDialogPlugin(),
            thematicBreakPlugin(),
            markdownShortcutPlugin(),
            maxLengthPlugin(4096),
            toolbarPlugin({
              toolbarContents: () => (
                <>
                  <UndoRedo />
                  <ListsToggle />
                  <BoldItalicUnderlineToggles />
                  <CreateLink />
                  <InsertAdmonition />
                  <InsertThematicBreak />
                </>
              ),
            }),
          ]}
          onChange={handleNoteChange}
          placeholder="Enter order notes here..."
        />
      </Paper>
      {lastSavedDate ? (
        <Typography variant="caption" color="success">
          <CheckIcon fontSize="inherit" /> Saved <DateDisplay date={lastSavedDate} />
        </Typography>
      ) : (
        <Typography variant="caption">
          Last modified <DateDisplay date={rfq?.dateModified} />
        </Typography>
      )}
    </Box>
  )
}
